import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';
import type { ZodSchema } from 'zod';
import { UploadCloud, FileText } from 'lucide-react';
import { apiOrThrow } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';

interface BulkUploadProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** API endpoint, e.g. '/admin/bulk/clients'. */
  endpoint: string;
  /** Query key to invalidate after successful upload. */
  invalidateKey: readonly unknown[];
  /** Display these column headers in the help text. */
  sampleHeaders: string[];
  /**
   * Optional per-row Zod schema for client-side preview validation.
   * If omitted, all parsed rows are sent and server validates.
   */
  rowSchema?: ZodSchema;
}

interface ParsedResult {
  validRows: Record<string, unknown>[];
  invalidRows: Array<{ row: number; error: string; raw: Record<string, unknown> }>;
  headers: string[];
}

interface UploadResponse {
  inserted: number;
  skipped: Array<{ row: number; reason: string }>;
}

/**
 * CSV bulk upload — parse client-side, preview, then submit JSON to API.
 *
 * Why CSV not Excel: papaparse is ~50KB, SheetJS is ~600KB and adds bundle
 * weight users feel on first paint. Most accountants can "Save as CSV" from
 * Excel in one click. If real Excel support becomes a hard requirement we
 * can add an opt-in dynamic import of SheetJS.
 */
export function BulkUpload({
  open,
  onClose,
  title,
  endpoint,
  invalidateKey,
  sampleHeaders,
  rowSchema,
}: BulkUploadProps) {
  const qc = useQueryClient();
  const [parsed, setParsed] = useState<ParsedResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setParsed(null);
    setResult(null);
    setError(null);
  };

  const handleFile = (file: File) => {
    reset();
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (parseResult) => {
        const validRows: Record<string, unknown>[] = [];
        const invalidRows: ParsedResult['invalidRows'] = [];
        for (const [i, raw] of parseResult.data.entries()) {
          // Coerce booleans + numbers from string cells
          const coerced = coerceRow(raw);
          if (rowSchema) {
            const r = rowSchema.safeParse(coerced);
            if (r.success) {
              validRows.push(r.data as Record<string, unknown>);
            } else {
              invalidRows.push({
                row: i + 2, // +1 for header, +1 for 1-indexed
                error: r.error.issues.map((iss) => `${iss.path.join('.')}: ${iss.message}`).join('; '),
                raw: coerced,
              });
            }
          } else {
            validRows.push(coerced);
          }
        }
        setParsed({
          validRows,
          invalidRows,
          headers: parseResult.meta.fields ?? [],
        });
      },
      error: (err) => setError(`Parse error: ${err.message}`),
    });
  };

  const submit = async () => {
    if (!parsed || parsed.validRows.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      const res = await apiOrThrow<UploadResponse>(endpoint, {
        method: 'POST',
        json: { rows: parsed.validRows },
      });
      setResult(res);
      qc.invalidateQueries({ queryKey: invalidateKey });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      size="xl"
      description={`Expected columns: ${sampleHeaders.join(', ')}`}
      footer={
        <>
          <Button variant="secondary" onClick={close} type="button">
            {result ? 'Done' : 'Cancel'}
          </Button>
          {parsed && !result && (
            <Button
              onClick={submit}
              loading={uploading}
              type="button"
              disabled={parsed.validRows.length === 0}
            >
              Upload {parsed.validRows.length} row{parsed.validRows.length === 1 ? '' : 's'}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        {!parsed && !result && (
          <label
            htmlFor="bulk-csv-input"
            className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-brand-gray-light bg-brand-off-white px-6 py-10 text-center hover:bg-brand-gray-light/40"
          >
            <UploadCloud className="h-8 w-8 text-brand-gray-mid" aria-hidden />
            <p className="text-sm font-medium text-brand-charcoal">
              Click to choose a CSV file
            </p>
            <p className="text-xs text-brand-gray-mid">
              First row must contain column headers
            </p>
            <input
              id="bulk-csv-input"
              type="file"
              accept=".csv,text/csv"
              className="sr-only"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </label>
        )}

        {error && <Alert tone="danger" title="Error">{error}</Alert>}

        {parsed && !result && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <Badge tone="success">{parsed.validRows.length} valid</Badge>
              {parsed.invalidRows.length > 0 && (
                <Badge tone="danger">{parsed.invalidRows.length} invalid</Badge>
              )}
              <span className="text-brand-gray-mid">
                Columns: {parsed.headers.join(', ')}
              </span>
            </div>

            {parsed.invalidRows.length > 0 && (
              <Alert tone="warning" title="Some rows failed validation and will be skipped:">
                <ul className="mt-1 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs">
                  {parsed.invalidRows.slice(0, 20).map((r) => (
                    <li key={r.row}>
                      <span className="font-mono">Row {r.row}</span>: {r.error}
                    </li>
                  ))}
                  {parsed.invalidRows.length > 20 && (
                    <li className="text-brand-gray-mid">
                      …and {parsed.invalidRows.length - 20} more
                    </li>
                  )}
                </ul>
              </Alert>
            )}

            {parsed.validRows.length > 0 && (
              <div className="overflow-hidden rounded border border-brand-gray-light">
                <div className="flex items-center gap-2 border-b border-brand-gray-light bg-brand-off-white px-3 py-2 text-xs font-medium text-brand-gray-mid">
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  Preview (first 5 valid rows)
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-brand-off-white text-brand-gray-mid">
                      <tr>
                        {parsed.headers.map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-semibold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-gray-light">
                      {parsed.validRows.slice(0, 5).map((r, i) => (
                        <tr key={i}>
                          {parsed.headers.map((h) => (
                            <td key={h} className="px-3 py-2 text-brand-charcoal">
                              {formatCell(r[h])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {result && (
          <Alert tone={result.skipped.length > 0 ? 'warning' : 'success'} title="Upload complete">
            <p>
              Inserted: <strong>{result.inserted}</strong>
              {result.skipped.length > 0 && (
                <>
                  {' · '}
                  Skipped: <strong>{result.skipped.length}</strong>
                </>
              )}
            </p>
            {result.skipped.length > 0 && (
              <ul className="mt-2 max-h-32 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs">
                {result.skipped.map((s) => (
                  <li key={s.row}>
                    <span className="font-mono">Row {s.row}</span>: {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function coerceRow(raw: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    const trimmed = v?.trim();
    if (trimmed === '' || trimmed === undefined) {
      out[k] = undefined;
      continue;
    }
    // Booleans
    if (trimmed.toLowerCase() === 'true' || trimmed === '1') {
      out[k] = true;
      continue;
    }
    if (trimmed.toLowerCase() === 'false' || trimmed === '0') {
      out[k] = false;
      continue;
    }
    // Numbers (only if the field name suggests numeric)
    if (/^(fiscalYear|year|hours|lat|lng|radiusM)$/.test(k) && /^-?\d+(\.\d+)?$/.test(trimmed)) {
      out[k] = Number(trimmed);
      continue;
    }
    out[k] = trimmed;
  }
  return out;
}

function formatCell(v: unknown): string {
  if (v === undefined || v === null) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  return String(v);
}
